// frontend/src/components/athleteEditor/OffersVisitsCard.jsx
//
// The athlete-detail page's "Offers & Visits" card. Read-only for coach/partner
// viewers; fully editable for admin/internal_staff (the page passes
// `canEdit = editMode && perms.canEditProfile`). Editing mirrors OffersPage.js
// (the athlete's own funnel editor) — same colleges.json autocomplete, same
// field set, same single-commitment rule, autosave on change/blur — but every
// mutating call carries `params: { as_athlete_id: userId }` so an admin edits
// the athlete being viewed, exactly like useMeasurableSaves.js does.
//
// Offer/visit enum values come straight from the backend (models.py):
//   offer_type   full | partial | pwo | verbal
//   visit_type   official | unofficial
//   visit_format game_day | junior_day | midweek | prospect_camp | overnight | virtual
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import CollegePicker from '../CollegePicker';

const OFFER_TYPE_OPTIONS = [
  { value: 'full', label: 'Full scholarship' },
  { value: 'partial', label: 'Partial scholarship' },
  { value: 'pwo', label: 'Preferred walk-on' },
  { value: 'verbal', label: 'Verbal' },
];

const VISIT_TYPE_OPTIONS = [
  { value: 'official', label: 'Official' },
  { value: 'unofficial', label: 'Unofficial' },
];

const VISIT_FORMAT_OPTIONS = [
  { value: 'game_day', label: 'Game day' },
  { value: 'junior_day', label: 'Junior day' },
  { value: 'midweek', label: 'Midweek' },
  { value: 'prospect_camp', label: 'Prospect camp' },
  { value: 'overnight', label: 'Overnight' },
  { value: 'virtual', label: 'Virtual' },
];

const MAX_OFFERS = 10;
const MAX_VISITS = 10;

const humanize = (s) => (s || '').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
const offerTypeLabel = (v) => (OFFER_TYPE_OPTIONS.find((o) => o.value === v) || {}).label || humanize(v);
const visitTypeLabel = (v) => (VISIT_TYPE_OPTIONS.find((o) => o.value === v) || {}).label || humanize(v);
const visitFormatLabel = (v) => (VISIT_FORMAT_OPTIONS.find((o) => o.value === v) || {}).label || humanize(v);

const nextFreeSlot = (rows, max) => {
  const used = rows.map((r) => r.slot_number);
  let slot = 1;
  while (used.includes(slot) && slot <= max) slot++;
  return slot > max ? null : slot;
};

const CommittedPill = ({ small }) => (
  <span
    style={{
      fontSize: small ? 10 : 11,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.02em',
      color: '#fff',
      background: '#1E7A46',
      padding: '2px 8px',
      borderRadius: 10,
      flexShrink: 0,
    }}
  >
    Committed
  </span>
);

export default function OffersVisitsCard({ userId, profile, canEdit, apiUrl, getConfig, onChange }) {
  const [offers, setOffers] = useState(() => profile?.offers || []);
  const [visits, setVisits] = useState(() => profile?.visits || []);


  // Re-seed local rows whenever the parent hands us fresh offers/visits — the
  // initial page load, an athlete switch, or our own onChange merged back in.
  useEffect(() => {
    setOffers(profile?.offers || []);
  }, [profile?.offers]);
  useEffect(() => {
    setVisits(profile?.visits || []);
  }, [profile?.visits]);

  // Editors only: load the college catalog for the autocomplete, and pull a
  // fresh copy of the rows straight from the dedicated endpoints (with
  // as_athlete_id) so we have server ids/logos/division to save against.

  useEffect(() => {
    if (!canEdit) return undefined;
    let cancelled = false;
    const cfg = {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      params: { as_athlete_id: userId },
    };
    Promise.all([
      axios.get(`${apiUrl}/athletes/offers`, cfg).catch(() => ({ data: [] })),
      axios.get(`${apiUrl}/athletes/visits`, cfg).catch(() => ({ data: [] })),
    ]).then(([oRes, vRes]) => {
      if (cancelled) return;
      setOffers(Array.isArray(oRes.data) ? oRes.data : []);
      setVisits(Array.isArray(vRes.data) ? vRes.data : []);
    });
    return () => { cancelled = true; };
  }, [canEdit, userId, apiUrl]);


  // After any successful mutation, re-pull the authoritative rows (fresh ids,
  // logos, division, derived `committed`) and push them up so the page's
  // profile stays in sync. Event-handler driven, so no exhaustive-deps concern.
  const reload = async () => {
    try {
      const cfg = { ...getConfig(), params: { as_athlete_id: userId } };
      const [oRes, vRes] = await Promise.all([
        axios.get(`${apiUrl}/athletes/offers`, cfg),
        axios.get(`${apiUrl}/athletes/visits`, cfg),
      ]);
      const freshOffers = Array.isArray(oRes.data) ? oRes.data : [];
      const freshVisits = Array.isArray(vRes.data) ? vRes.data : [];
      setOffers(freshOffers);
      setVisits(freshVisits);
      if (onChange) onChange({ offers: freshOffers, visits: freshVisits });
    } catch (err) {
      console.error('Error reloading offers/visits:', err);
    }
  };

  // ── Offers ────────────────────────────────────────────────────────────
  const updateOfferField = (slot, field, value) =>
    setOffers((prev) => prev.map((o) => (o.slot_number === slot ? { ...o, [field]: value } : o)));

  // Typing into the college box replaces any earlier pick, so the old college's
  // id and location are dropped with the first keystroke (the picker does the
  // same). Explicit `unitid: null` is what tells the server to clear it.
  const typeOfferCollege = (slot, text) =>
    setOffers((prev) => prev.map((o) => (o.slot_number === slot
      ? { ...o, college_name: text, unitid: null, college_city: '', college_state: '' } : o)));

  const saveOffer = async (offer) => {
    if (!offer.college_name) return; // upsert only once a school is chosen
    try {
      await axios.post(`${apiUrl}/athletes/offers`, offer, {
        ...getConfig(),
        params: { as_athlete_id: userId },
      });
      await reload();
    } catch (err) {
      console.error('Error saving offer:', err);
    }
  };

  const handleAddOffer = () => {
    const slot = nextFreeSlot(offers, MAX_OFFERS);
    if (slot == null) return;
    setOffers((prev) => [
      ...prev,
      {
        slot_number: slot,
        college_name: '',
        college_city: '',
        college_state: '',
        sport: profile?.sport || 'Football',
        offer_type: 'full',
        offer_date: '',
        amount: '',
        notes: '',
        committed: false,
      },
    ]);
  };

  const handleDeleteOffer = async (offer) => {
    if (!window.confirm(`Remove the offer from ${offer.college_name || 'this college'}?`)) return;
    if (offer.id) {
      try {
        await axios.delete(`${apiUrl}/athletes/offers/${offer.id}`, {
          ...getConfig(),
          params: { as_athlete_id: userId },
        });
        await reload();
      } catch (err) {
        console.error('Error deleting offer:', err);
      }
      return;
    }
    setOffers((prev) => prev.filter((o) => o.slot_number !== offer.slot_number));
  };

  // One school at a time: checking one offer clears `committed` on the others
  // locally; the POST carries the flag and the backend enforces the same rule.
  const setCommitted = (offer, checked) => {
    if (!offer.college_name) return;
    setOffers((prev) =>
      prev.map((o) => ({
        ...o,
        committed: o.slot_number === offer.slot_number ? checked : checked ? false : o.committed,
      })),
    );
    saveOffer({ ...offer, committed: checked });
  };

  // ── Visits ────────────────────────────────────────────────────────────
  const updateVisitField = (slot, field, value) =>
    setVisits((prev) => prev.map((v) => (v.slot_number === slot ? { ...v, [field]: value } : v)));

  const typeVisitCollege = (slot, text) =>
    setVisits((prev) => prev.map((v) => (v.slot_number === slot
      ? { ...v, college_name: text, unitid: null } : v)));

  const saveVisit = async (visit) => {
    if (!visit.college_name) return;
    try {
      await axios.post(`${apiUrl}/athletes/visits`, visit, {
        ...getConfig(),
        params: { as_athlete_id: userId },
      });
      await reload();
    } catch (err) {
      console.error('Error saving visit:', err);
    }
  };

  const handleAddVisit = () => {
    const slot = nextFreeSlot(visits, MAX_VISITS);
    if (slot == null) return;
    setVisits((prev) => [
      ...prev,
      {
        slot_number: slot,
        college_name: '',
        visit_date: '',
        visit_end_date: '',
        visit_type: 'unofficial',
        visit_format: 'game_day',
      },
    ]);
  };

  const handleDeleteVisit = async (visit) => {
    if (!window.confirm(`Remove the visit to ${visit.college_name || 'this college'}?`)) return;
    if (visit.id) {
      try {
        await axios.delete(`${apiUrl}/athletes/visits/${visit.id}`, {
          ...getConfig(),
          params: { as_athlete_id: userId },
        });
        await reload();
      } catch (err) {
        console.error('Error deleting visit:', err);
      }
      return;
    }
    setVisits((prev) => prev.filter((v) => v.slot_number !== visit.slot_number));
  };

  // ── College autocomplete ──────────────────────────────────────────────
  const selectOfferCollege = (slot, college) => {
    const offer = offers.find((o) => o.slot_number === slot);
    const patch = {
      unitid: college.u,
      college_name: college.n,
      college_city: college.c,
      college_state: college.s,
    };
    setOffers((prev) => prev.map((o) => (o.slot_number === slot ? { ...o, ...patch } : o)));
    if (offer) saveOffer({ ...offer, ...patch });
  };

  const selectVisitCollege = (slot, college) => {
    const visit = visits.find((v) => v.slot_number === slot);
    const patch = { unitid: college.u, college_name: college.n };
    setVisits((prev) => prev.map((v) => (v.slot_number === slot ? { ...v, ...patch } : v)));
    if (visit) saveVisit({ ...visit, ...patch });
  };

  // ── Committed banner (read mode) ──────────────────────────────────────
  const committedOffer = offers.find((o) => o.committed);
  const committedName = committedOffer?.college_name || profile?.committed_college_name;
  const committedLoc = (committedOffer
    ? [committedOffer.college_city, committedOffer.college_state]
    : [profile?.committed_college_city, profile?.committed_college_state]
  )
    .filter(Boolean)
    .join(', ');
  const commitDate = profile?.commitment_date;

  const visitDateText = (v) =>
    [v.visit_date, v.visit_end_date].filter(Boolean).join(' – ');

  return (
    <div className="ae-card ae-card--pad">
      {/* ── Offers & Commitment ─────────────────────────────────────── */}
      <div className="form-section">
        <div className="form-section__title">Offers &amp; Commitment</div>

        {!canEdit && committedName && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 12px',
              marginBottom: 12,
              borderRadius: 8,
              background: 'rgba(30,122,70,0.10)',
              border: '1px solid rgba(30,122,70,0.25)',
            }}
          >
            <CommittedPill />
            <span style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>
              {committedName}
              {committedLoc ? ` · ${committedLoc}` : ''}
              {commitDate ? (
                <span style={{ fontWeight: 400, color: '#64748b' }}> · {commitDate}</span>
              ) : null}
            </span>
          </div>
        )}

        {!canEdit ? (
          offers.length === 0 ? (
            <div style={{ fontSize: 13, color: '#94a3b8' }}>No offers on record</div>
          ) : (
            <div>
              {offers.map((o) => (
                <div
                  key={o.id ?? o.slot_number}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    padding: '8px 0',
                    borderBottom: '1px solid #eef2f6',
                  }}
                >
                  {o.logo_url ? (
                    <img src={o.logo_url} alt="" style={{ width: 28, height: 28, objectFit: 'contain', flexShrink: 0 }} />
                  ) : null}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{o.college_name}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>
                      {[offerTypeLabel(o.offer_type), o.division].filter(Boolean).join(' · ') || '—'}
                    </div>
                    {(o.offer_date || o.amount) && (
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                        {[o.offer_date, o.amount].filter(Boolean).join(' · ')}
                      </div>
                    )}
                    {o.notes ? (
                      <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2, whiteSpace: 'pre-wrap' }}>{o.notes}</div>
                    ) : null}
                  </div>
                  {o.committed && <CommittedPill small />}
                </div>
              ))}
            </div>
          )
        ) : (
          <>
            {offers.map((offer) => (
              <div
                key={offer.slot_number}
                style={{ padding: '12px 0', borderTop: '1px solid #eef2f6' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#94a3b8' }}>
                    Offer {offer.slot_number}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleDeleteOffer(offer)}
                    className="form-field__button"
                    style={{ background: 'none', border: 'none', color: '#b91c1c', cursor: 'pointer', fontSize: 13, padding: '2px 4px' }}
                    aria-label={`Delete offer ${offer.slot_number}`}
                  >
                    Delete
                  </button>
                </div>

                <div className="form-field" style={{ position: 'relative' }}>
                  <label className="form-field__label" htmlFor={`offer-college-${offer.slot_number}`}>College / University</label>
                  <CollegePicker
                    inputId={`offer-college-${offer.slot_number}`}
                    value={offer.college_name}
                    onInputChange={(text) => typeOfferCollege(offer.slot_number, text)}
                    onSelect={(c) => selectOfferCollege(offer.slot_number, c)}
                    onCommitText={() => saveOffer(offers.find((o) => o.slot_number === offer.slot_number) || offer)}
                  />
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <label className="form-field__label" htmlFor={`offer-type-${offer.slot_number}`}>Offer Type</label>
                    <select
                      id={`offer-type-${offer.slot_number}`}
                      value={offer.offer_type || 'full'}
                      onChange={(e) => {
                        updateOfferField(offer.slot_number, 'offer_type', e.target.value);
                        if (offer.college_name) saveOffer({ ...offer, offer_type: e.target.value });
                      }}
                      className="form-field__select"
                    >
                      {OFFER_TYPE_OPTIONS.map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label className="form-field__label" htmlFor={`offer-date-${offer.slot_number}`}>Offer Date</label>
                    <input
                      id={`offer-date-${offer.slot_number}`}
                      type="date"
                      value={offer.offer_date || ''}
                      onChange={(e) => {
                        updateOfferField(offer.slot_number, 'offer_date', e.target.value);
                        if (offer.college_name) saveOffer({ ...offer, offer_date: e.target.value });
                      }}
                      className="form-field__input"
                    />
                  </div>
                </div>

                <div className="form-field">
                  <label className="form-field__label" htmlFor={`offer-amount-${offer.slot_number}`}>Scholarship amount (optional)</label>
                  <input
                    id={`offer-amount-${offer.slot_number}`}
                    type="text"
                    value={offer.amount || ''}
                    onChange={(e) => updateOfferField(offer.slot_number, 'amount', e.target.value)}
                    onBlur={() => { if (offer.college_name) saveOffer(offers.find((o) => o.slot_number === offer.slot_number) || offer); }}
                    className="form-field__input"
                    placeholder="e.g. $50,000 / year"
                  />
                </div>

                <div className="form-field">
                  <label className="form-field__label" htmlFor={`offer-notes-${offer.slot_number}`}>Notes</label>
                  <textarea
                    id={`offer-notes-${offer.slot_number}`}
                    value={offer.notes || ''}
                    onChange={(e) => updateOfferField(offer.slot_number, 'notes', e.target.value)}
                    onBlur={() => { if (offer.college_name) saveOffer(offers.find((o) => o.slot_number === offer.slot_number) || offer); }}
                    className="form-field__input"
                    rows={2}
                    placeholder="Optional notes about this offer..."
                  />
                </div>

                <label
                  className="form-field__checkbox"
                  style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: offer.college_name ? 'pointer' : 'not-allowed' }}
                >
                  <input
                    type="checkbox"
                    checked={!!offer.committed}
                    disabled={!offer.college_name}
                    onChange={(e) => setCommitted(offer, e.target.checked)}
                    aria-label={`Committed to ${offer.college_name || 'this school'}`}
                  />
                  <span>Committed to this school</span>
                </label>
              </div>
            ))}

            {offers.length === 0 && (
              <div style={{ fontSize: 13, color: '#94a3b8', padding: '4px 0' }}>No offers on record</div>
            )}
            {offers.length < MAX_OFFERS && (
              <button
                type="button"
                onClick={handleAddOffer}
                style={{ marginTop: 10, background: 'none', border: '1px dashed #cbd5e1', borderRadius: 8, color: '#334155', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: '8px 12px' }}
              >
                + Add offer
              </button>
            )}
          </>
        )}
      </div>

      {/* ── Visits ──────────────────────────────────────────────────── */}
      <div className="form-section" style={{ marginTop: 20 }}>
        <div className="form-section__title">Visits</div>

        {!canEdit ? (
          visits.length === 0 ? (
            <div style={{ fontSize: 13, color: '#94a3b8' }}>No visits on record</div>
          ) : (
            <div>
              {visits.map((v) => (
                <div
                  key={v.id ?? v.slot_number}
                  style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderBottom: '1px solid #eef2f6' }}
                >
                  {v.logo_url ? (
                    <img src={v.logo_url} alt="" style={{ width: 28, height: 28, objectFit: 'contain', flexShrink: 0 }} />
                  ) : null}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{v.college_name}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>
                      {[visitTypeLabel(v.visit_type), visitFormatLabel(v.visit_format), v.division].filter(Boolean).join(' · ') || '—'}
                    </div>
                    {visitDateText(v) ? (
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{visitDateText(v)}</div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          <>
            {visits.map((visit) => (
              <div key={visit.slot_number} style={{ padding: '12px 0', borderTop: '1px solid #eef2f6' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#94a3b8' }}>
                    Visit {visit.slot_number}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleDeleteVisit(visit)}
                    style={{ background: 'none', border: 'none', color: '#b91c1c', cursor: 'pointer', fontSize: 13, padding: '2px 4px' }}
                    aria-label={`Delete visit ${visit.slot_number}`}
                  >
                    Delete
                  </button>
                </div>

                <div className="form-field" style={{ position: 'relative' }}>
                  <label className="form-field__label" htmlFor={`visit-college-${visit.slot_number}`}>College / University</label>
                  <CollegePicker
                    inputId={`visit-college-${visit.slot_number}`}
                    value={visit.college_name}
                    onInputChange={(text) => typeVisitCollege(visit.slot_number, text)}
                    onSelect={(c) => selectVisitCollege(visit.slot_number, c)}
                    onCommitText={() => saveVisit(visits.find((v) => v.slot_number === visit.slot_number) || visit)}
                  />
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <label className="form-field__label" htmlFor={`visit-date-${visit.slot_number}`}>Visit Date</label>
                    <input
                      id={`visit-date-${visit.slot_number}`}
                      type="date"
                      value={visit.visit_date || ''}
                      onChange={(e) => {
                        updateVisitField(visit.slot_number, 'visit_date', e.target.value);
                        if (visit.college_name) saveVisit({ ...visit, visit_date: e.target.value });
                      }}
                      className="form-field__input"
                    />
                  </div>
                  <div className="form-field">
                    <label className="form-field__label" htmlFor={`visit-type-${visit.slot_number}`}>Visit Type</label>
                    <select
                      id={`visit-type-${visit.slot_number}`}
                      value={visit.visit_type || 'unofficial'}
                      onChange={(e) => {
                        updateVisitField(visit.slot_number, 'visit_type', e.target.value);
                        if (visit.college_name) saveVisit({ ...visit, visit_type: e.target.value });
                      }}
                      className="form-field__select"
                    >
                      {VISIT_TYPE_OPTIONS.map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  {visit.visit_type === 'official' && (
                    <div className="form-field">
                      <label className="form-field__label" htmlFor={`visit-end-${visit.slot_number}`}>Visit End Date</label>
                      <input
                        id={`visit-end-${visit.slot_number}`}
                        type="date"
                        value={visit.visit_end_date || ''}
                        onChange={(e) => {
                          updateVisitField(visit.slot_number, 'visit_end_date', e.target.value);
                          if (visit.college_name) saveVisit({ ...visit, visit_end_date: e.target.value });
                        }}
                        className="form-field__input"
                      />
                    </div>
                  )}
                  <div className="form-field">
                    <label className="form-field__label" htmlFor={`visit-format-${visit.slot_number}`}>Visit Format</label>
                    <select
                      id={`visit-format-${visit.slot_number}`}
                      value={visit.visit_format || 'game_day'}
                      onChange={(e) => {
                        updateVisitField(visit.slot_number, 'visit_format', e.target.value);
                        if (visit.college_name) saveVisit({ ...visit, visit_format: e.target.value });
                      }}
                      className="form-field__select"
                    >
                      {VISIT_FORMAT_OPTIONS.map((f) => (
                        <option key={f.value} value={f.value}>{f.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            ))}

            {visits.length === 0 && (
              <div style={{ fontSize: 13, color: '#94a3b8', padding: '4px 0' }}>No visits on record</div>
            )}
            {visits.length < MAX_VISITS && (
              <button
                type="button"
                onClick={handleAddVisit}
                style={{ marginTop: 10, background: 'none', border: '1px dashed #cbd5e1', borderRadius: 8, color: '#334155', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: '8px 12px' }}
              >
                + Add visit
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
